import React, { useState } from 'react';
import {
  Alert,
  Box,
  Divider,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { AgronodTypography } from '@agronod/mui-components';

import type { FarmProfile, FilterRule } from '@/types/farm-profile';
import type { DocumentRequirement } from '@/types';
import { applyFarmProfileFilter } from '@/utils/farm-profile/applyFilter';
import {
  FILTER_FIELDS,
} from '@/constants/farm-profile/filter-rule';
import ChipSelectButton from '@/components/farm-profile/steps/ChipSelectButton';

interface FilterRulePreviewProps {
  /** The current rule being edited. Null when no conditions exist. */
  rule: FilterRule | null;
}

/** Initial mock profile — every field empty so the admin can toggle values on. */
const DEFAULT_MOCK_PROFILE: FarmProfile = {
  id: 'preview',
  agronodkontoId: 'preview',
  userId: 'preview',
  productionTypes: [],
  nonFoodAnimals: [],
  combinatorBusiness: [],
  ecoStatus: null,
  animalUnits: null,
  animalUnitsBreakdown: null,
  acreageOwned: null,
  acreageLeased: null,
  geographicFactors: [],
  certifications: [],
  controlPrograms: [],
  otherNotifiable: [],
  isCompleted: true,
  isSkipped: false,
  completedAt: null,
  createdAt: '',
  updatedAt: '',
};

/**
 * FilterRulePreview — renders an editable mock FarmProfile and evaluates the
 * current filter rule against it using the same `applyFarmProfileFilter` engine
 * that runs in production.
 *
 * The preview is purely local — no network calls, no persistence.
 *
 * Algorithm:
 * 1. If rule is null or empty, show a placeholder Alert.
 * 2. Build a synthetic DocumentRequirement stub containing only the rule.
 * 3. Pass [stub] + mockProfile into applyFarmProfileFilter.
 * 4. Show "Skulle döljas" (info) or "Skulle visas" (success) based on result.
 *
 * @param rule The FilterRule to preview, or null when no conditions are set.
 */
const FilterRulePreview: React.FC<FilterRulePreviewProps> = ({ rule }) => {
  const [mockProfile, setMockProfile] = useState<FarmProfile>(DEFAULT_MOCK_PROFILE);

  if (!rule || rule.conditions.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Lägg till villkor ovan för att förhandsgranska hur regeln påverkar en exempelprofil.
      </Alert>
    );
  }

  // Synthetic requirement — only filterRule matters for the evaluation.
  const syntheticReq: DocumentRequirement = {
    id: 'preview',
    tags: [],
    isRequired: true,
    description: '',
    displayOrder: 0,
    yearOffsets: [],
    coverageRule: 'ALL_YEARS',
    filterRule: rule,
  };

  const { hidden } = applyFarmProfileFilter([syntheticReq], mockProfile);
  const isHidden = hidden.length > 0;
  const hiddenReason = isHidden ? hidden[0]?.reason : null;

  const toggleMulti = (field: keyof FarmProfile, option: string) => {
    setMockProfile((prev) => {
      const current = (prev[field] as string[]) ?? [];
      const next = current.includes(option)
        ? current.filter((v) => v !== option)
        : [...current, option];
      return { ...prev, [field]: next };
    });
  };

  // Ordered list of profile fields to render in the mock form.
  // We skip `number` domain here to avoid nesting complexity — animalUnits gets its own input.
  const multiFields = FILTER_FIELDS.filter((f) => f.domain === 'multi');
  const ecoStatusField = FILTER_FIELDS.find((f) => f.key === 'ecoStatus');
  const animalUnitsField = FILTER_FIELDS.find((f) => f.key === 'animalUnits');

  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
      <AgronodTypography variant="subtitle2" gutterBottom>
        Förhandsgranskning — testprofil
      </AgronodTypography>
      <AgronodTypography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2
        }}>
        Justera exempelprofilen nedan för att se när kravet skulle döljas eller visas.
      </AgronodTypography>

      <Stack spacing={2}>
        {/* Multi-domain chip selects */}
        {multiFields.map((fieldDef) => {
          if (!fieldDef.options) return null;
          const current = (mockProfile[fieldDef.key as keyof FarmProfile] as string[]) ?? [];
          return (
            <Box key={fieldDef.key}>
              <AgronodTypography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  mb: 0.5,
                  display: 'block'
                }}>
                {fieldDef.label}
              </AgronodTypography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {fieldDef.options.map((option) => (
                  <ChipSelectButton
                    key={option}
                    label={option}
                    checked={current.includes(option)}
                    onClick={() => toggleMulti(fieldDef.key as keyof FarmProfile, option)}
                  />
                ))}
              </Box>
            </Box>
          );
        })}

        {/* Djurenheter */}
        {animalUnitsField && (
          <TextField
            label={animalUnitsField.label}
            type="number"
            size="small"
            value={mockProfile.animalUnits ?? ''}
            onChange={(e) =>
              setMockProfile((prev) => ({
                ...prev,
                animalUnits: e.target.value ? Number(e.target.value) : null,
              }))
            }
            sx={{ width: 200 }}
            helperText="Lämna tomt för ingen uppgift"
            slotProps={{
              htmlInput: { min: 0, step: 1 }
            }}
          />
        )}

        {/* Ekostatus */}
        {ecoStatusField && ecoStatusField.options && (
          <TextField
            select
            label={ecoStatusField.label}
            size="small"
            value={mockProfile.ecoStatus ?? ''}
            onChange={(e) =>
              setMockProfile((prev) => ({
                ...prev,
                ecoStatus: e.target.value || null,
              }))
            }
            sx={{ width: 200 }}
          >
            <MenuItem value="">
              <em>Ingen uppgift</em>
            </MenuItem>
            {ecoStatusField.options.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Result */}
      {isHidden ? (
        <Alert severity="info">
          <strong>Kravet skulle döljas</strong>
          {hiddenReason && (
            <>
              {' '}— meddelande till lantbrukaren: <em>"{hiddenReason}"</em>
            </>
          )}
        </Alert>
      ) : (
        <Alert severity="success">
          Kravet skulle <strong>visas</strong> för denna profil.
        </Alert>
      )}
    </Box>
  );
};

export default FilterRulePreview;
